import React, { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Menu, X } from 'lucide-react'
import LanguageSwitcher from '../LanguageSwitcher'

interface User {
    accessToken?: string
    firstName?: string
    picture?: string
}

interface HeaderProps {
    user: User
}

const Header = ({
    user: {
        accessToken,
    },
}: HeaderProps) => {
    const { t } = useTranslation()
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [resourcesOpen, setResourcesOpen] = useState(false)

    const handleLogin = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault()
        window.location.href = '/auth/redirect'
    }

    useEffect(() => {
        document.title = 'MV Data Center'
    }, [])

    return (
        <header className="sticky top-0 z-50 w-full bg-[hsl(220,26%,14%)]">
            <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
                <Link to="/" className="flex items-center">
                    <img src="/assets/mv-data-logo-flat.png" className="h-8 brightness-0 invert" alt="MV Data" />
                </Link>

                {/* Desktop nav */}
                <nav className="hidden md:flex items-center gap-6">
                    <Link to="/" className="text-sm font-medium text-white/80 hover:text-white transition-colors">
                        {t('nav.home')}
                    </Link>
                    <Link to="/contact" className="text-sm font-medium text-white/80 hover:text-white transition-colors">
                        {t('nav.contact')}
                    </Link>
                    <div className="relative"
                        onMouseEnter={() => setResourcesOpen(true)}
                        onMouseLeave={() => setResourcesOpen(false)}
                    >
                        <button className="inline-flex items-center gap-1 text-sm font-medium text-white/80 hover:text-white transition-colors">
                            {t('nav.resources')}
                            <ChevronDown className="h-4 w-4" />
                        </button>
                        {resourcesOpen && (
                            <div className="absolute top-full left-0 mt-1 w-44 rounded-md border bg-popover p-1 shadow-md">
                                <Link to="/documentation" className="block rounded-sm px-3 py-2 text-sm hover:bg-accent transition-colors" onClick={() => setResourcesOpen(false)}>
                                    {t('nav.documentation')}
                                </Link>
                                <Link to="/pricing" className="block rounded-sm px-3 py-2 text-sm hover:bg-accent transition-colors" onClick={() => setResourcesOpen(false)}>
                                    {t('nav.pricing')}
                                </Link>
                                <Link to="/about" className="block rounded-sm px-3 py-2 text-sm hover:bg-accent transition-colors" onClick={() => setResourcesOpen(false)}>
                                    {t('nav.about')}
                                </Link>
                            </div>
                        )}
                    </div>
                    <LanguageSwitcher />
                    <a
                        href="/auth/redirect"
                        onClick={handleLogin}
                        className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                    >
                        {t('nav.login')}
                    </a>
                </nav>

                {/* Mobile menu button */}
                <button
                    className="md:hidden p-2 text-white/80 hover:text-white"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                    {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            {/* Mobile nav */}
            {mobileMenuOpen && (
                <div className="md:hidden border-t border-white/10 bg-[hsl(220,26%,14%)] px-4 py-4 space-y-3">
                    <Link to="/" className="block text-sm font-medium text-white/80 hover:text-white" onClick={() => setMobileMenuOpen(false)}>
                        {t('nav.home')}
                    </Link>
                    <Link to="/contact" className="block text-sm font-medium text-white/80 hover:text-white" onClick={() => setMobileMenuOpen(false)}>
                        {t('nav.contact')}
                    </Link>
                    <Link to="/documentation" className="block text-sm font-medium text-white/80 hover:text-white" onClick={() => setMobileMenuOpen(false)}>
                        {t('nav.documentation')}
                    </Link>
                    <Link to="/pricing" className="block text-sm font-medium text-white/80 hover:text-white" onClick={() => setMobileMenuOpen(false)}>
                        {t('nav.pricing')}
                    </Link>
                    <Link to="/about" className="block text-sm font-medium text-white/80 hover:text-white" onClick={() => setMobileMenuOpen(false)}>
                        {t('nav.about')}
                    </Link>
                    <div className="pt-2 border-t border-white/10">
                        <LanguageSwitcher />
                    </div>
                    <a
                        href="/auth/redirect"
                        onClick={handleLogin}
                        className="block w-full text-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                    >
                        {t('nav.login')}
                    </a>
                </div>
            )}
        </header>
    )
}

export default Header
